/**
 * @file   src\Pages\AddAdminSubscription.tsx
 * @brief  Add Admin Subscription page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
    React, 
    Container, 
    Breadcrumb, 
    Button, 
    Row, 
    Col, 
    useOutletContext, 
    useNavigate, 
    useEffect, 
    useState, 
    useParams 
} from '../utils/thirdpartyComponents';
import CWInput from '../components/CWInput';
import CWInputGroup from '../components/CWInputGroup';
import CWSelect from '../components/CWSelect';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { AppContextType } from '../interfaces/generalInterface';
import { addAdminSubscriptionSchema } from '../validations/addAdminSubscriptionSchema';
import { CHARGE_MAX_LENGTH, COMMISSION_MAX_LENGTH, DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from '../utils/constants';
import axios from '../config/axiosConfig';
import { SubscriptionApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';
import { AddOwnerSubscriptionItem } from '../interfaces/ownerSubscriptionInterface';
import CWTextarea from '../components/CWTextarea';
import { CommonMessages } from '../messages/validationMessages';

const AddAdminSubscription = (props: any) => {

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const [loading, setLoading] = useState(false);

    const toaster = new MessageToaster();

    useEffect(() => {
        handleUpdate()
        if (props.edit) {
            getOwnerSubscriptionData()
        }
    }, [])

    // Update the data
    const handleUpdate = async () => {
        await updateData({ heading: props.edit ? "Edit Subscription" : "Add New Subscription", url: `/superadmin/${pageURLs.manage_admin_subscription}`, showBackButton: true, showMainMenu: true, activeLink: "manage-admin-subscriptions" });
    };

    const [formData, setFormData] = useState({
        subscriptionname: '',
        description: '',
        from: '',
        to: '',
        chargeType: null,
        charge: '',

    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        subscriptionname: '',
        description: '',
        from: '',
        to: '',
        chargeType: '',
        charge: '',

    });

    const [options, setOptions] = useState([
        { label: '%', value: 1 },
        { label: '$', value: 2 },
    ]);

    const param = useParams()

    // Get details of subscription
    const getOwnerSubscriptionData = async () => {
        try {
            setLoading(true)
            const { data } = await axios.get(SubscriptionApi.GET_OWNER_SUBSCRIPTION_DETAILS + param.id)
            if (data.error === 0) {

                const subscription = data.data

                const subscriptionData = {
                    subscriptionname: subscription.subscriptionName,
                    description: subscription.description,
                    from: subscription.slabFrom,
                    to: subscription.slabTo,
                    chargeType: subscription.chargeType,
                    charge: subscription.charge
                }
                setFormData(subscriptionData)
                setLoading(false)
            }
            else {
                // show error message
                toaster.toastError(data.message);
                setLoading(false)

            }
        } catch (error: any) {
            console.error('Error fetching subscription information:', error.message);
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);

        }
    }

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {

        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        // validate to is not less than from value
        if (name === 'from' || name === 'to') {
            const fromValue = name === 'from' ? value : parseInt(formData.from);
            const toValue = name === 'to' ? value : parseInt(formData.to);


            if (toValue < fromValue) {
                setErrors((prev) => ({
                    ...prev,
                    to: CommonMessages.not_less_than,
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    to: '',
                }));
            }
        }

        // Should not allow more than 100 if % is chargetype
        if (name === "charge" && formData.chargeType === 1 && Number(value) >= 100) {
            setErrors((prev) => ({
                ...prev,
                charge: CommonMessages.percentage_limit,
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                charge: '',
            }));
        }

        if (name === "charge" || name === "from" || name === "to") {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^\d{0,8}(\.\d{0,2})?$/;

            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: value,
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }

    }

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        // To validate charge in case of %
        if (name === "chargeType" && Number(formData.charge) >= 100 && Number(selectedOption.value) === 1) {
            setErrors((prev) => ({
                ...prev,
                charge: CommonMessages.percentage_limit,
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                charge: '',
            }));
        }


        setFormData({
            ...formData,
            [name]: value,
        });

    };

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {

        event.preventDefault()
        try {
            // Validate form data
            await addAdminSubscriptionSchema.validate(formData, { abortEarly: false });
            let newformData: AddOwnerSubscriptionItem = {
                subscriptionName: formData.subscriptionname,
                description: formData.description,
                slabFrom: parseInt(formData.from),
                slabTo: parseInt(formData.to),
                chargeType: formData.chargeType,
                charge: parseInt(formData.charge)
            }

            try {
                setLoading(true)
                let data:any;
                if (props.edit) {
                    newformData = {
                        subscriptionId: param.id ? parseInt(param.id) : 0,
                        ...newformData
                    }
                     data = await axios.post(SubscriptionApi.EDIT_OWNER_SUBSCRIPTION, newformData);
                } else {
                     data = await axios.post(SubscriptionApi.CREATE_OWNER_SUBSCRIPTION, newformData);
                }

                setLoading(false)

                if (data?.error === 0) {
                    //Clear errors
                    setErrors({
                        subscriptionname: '',
                        description: '',
                        from: '',
                        to: '',
                        chargeType: '',
                        charge: '',
                    });

                    navigate(`/superadmin/${pageURLs.manage_admin_subscription}`)

                }
                else {
                    // console.log(data?.data?.message)
                    // show error message
                    toaster.toastError(data?.data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }


        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }

    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    return (

        <><div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="nav-btn-title-sec d-flex justify-content-between align-items-center">
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_admin_subscription}`) }}> Manage Subscription</Breadcrumb.Item>
                        <Breadcrumb.Item active>{props.edit ? "Edit Subscription" : "Add New Subscription"}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <h6 className='f-bold title'>Add New Subscription</h6>
                <form onSubmit={(e) => handleSubmit(e)} >

                    <div className="hardware-list-wrap">
                        <div className="form-inner mx-auto">
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="subscriptionname"
                                        name="subscriptionname"
                                        type="text"
                                        label="Subscription Name"
                                        onChange={handleInputChange}
                                        errorMessage={errors.subscriptionname}
                                        maxLength={NAME_MAX_LENGTH}
                                        value={formData.subscriptionname}
                                        isMandatory

                                    />
                                </Col>
                                <Col md={6}>
                                    <CWTextarea
                                        id="description"
                                        name="description"
                                        type="text"
                                        label="Description"
                                        onChange={handleInputChange}
                                        maxLength={DESCRIPTION_MAX_LENGTH}
                                        errorMessage={errors.description}
                                        value={formData.description}
                                        isMandatory
                                    />
                                </Col>
                            </Row>
                            <h5 className='title f-bold'>Add Commission Slabs</h5>
                            <Row>
                                <Col md={6}>
                                    <Row>
                                        <Col md={6}>
                                            <CWInputGroup
                                                id="from"
                                                name="from"
                                                type="number"
                                                label="From"
                                                labelPrefix="$"
                                                onChange={handleInputChange}
                                                maxLength={COMMISSION_MAX_LENGTH}
                                                errorMessage={errors.from}
                                                value={formData.from}
                                                isMandatory
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <CWInputGroup
                                                id="to"
                                                name="to"
                                                type="number"
                                                label="To"
                                                labelPrefix="$"
                                                onChange={handleInputChange}
                                                maxLength={COMMISSION_MAX_LENGTH}
                                                errorMessage={errors.to}
                                                value={formData.to}
                                                isMandatory
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Row>
                                        <Col md={6}>
                                            <CWSelect
                                                id="chargeType"
                                                name="chargeType"
                                                options={options}
                                                dropdownGap={0}
                                                dropdownPosition="auto"
                                                multi={false}
                                                label="% or $"
                                                values={options.filter(option => option.value === formData.chargeType)}
                                                errorMessage={errors.chargeType}
                                                onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'chargeType')}
                                                searchable={false}
                                                className='no-input'
                                                isMandatory
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <CWInputGroup
                                                id="charge"
                                                name="charge"
                                                type="number"
                                                label="Charge"
                                                maxLength={CHARGE_MAX_LENGTH}
                                                onChange={handleInputChange}
                                                errorMessage={errors.charge}
                                                value={formData.charge}
                                                isMandatory
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                    </div>
                    <div className="btn-sec d-flex justify-content-end">
                        <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.manage_admin_subscription}`)}>Cancel</Button>
                        <Button title="Save" variant="primary" className="primary-btn" type="submit">Save</Button>
                    </div>
                </form>
            </Container >

            {loading && <SmallLoader />}
        </div ></>
    )
}

export default AddAdminSubscription;
